import React, { useEffect, useRef, useState, useCallback } from "react";
import axios from "axios";
import { Upload, Trash2, Star, Loader2, ImageIcon, X, Check } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICES = [
  "artificial-grass", "patios", "decking", "fencing",
  "garden-design", "garden-makeovers", "commercial-landscaping",
];
const LOCATIONS = [
  "edinburgh", "glasgow", "falkirk", "livingston", "stirling", "hamilton",
  "motherwell", "east-kilbride", "cumbernauld", "dunfermline", "midlothian",
  "west-lothian", "lanarkshire",
];

export default function PhotosManager({ token }) {
  const [photos, setPhotos] = useState([]);
  const [total, setTotal] = useState(0);
  const [filterService, setFilterService] = useState("");
  const [filterLocation, setFilterLocation] = useState("");
  const [loading, setLoading] = useState(false);
  const [uploads, setUploads] = useState([]); // current upload progress
  const fileInput = useRef(null);

  const headers = { Authorization: `Bearer ${token}` };

  const fetchPhotos = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterService) params.service = filterService;
      if (filterLocation) params.location = filterLocation;
      const { data } = await axios.get(`${API}/photos`, { params });
      setPhotos(data.photos || []);
      setTotal(data.total || 0);
    } catch {
      toast.error("Could not load photos");
    } finally {
      setLoading(false);
    }
  }, [filterService, filterLocation]);

  useEffect(() => { fetchPhotos(); }, [fetchPhotos]);

  const handleFiles = async (files) => {
    const list = Array.from(files);
    if (!list.length) return;
    const initial = list.map((f, i) => ({ id: `${Date.now()}-${i}`, name: f.name, status: "pending", progress: 0 }));
    setUploads(initial);

    for (let i = 0; i < list.length; i++) {
      const file = list[i];
      const fd = new FormData();
      fd.append("file", file);
      fd.append("title", file.name.replace(/\.[^.]+$/, ""));
      fd.append("service", filterService || "");
      fd.append("location", filterLocation || "");

      setUploads((prev) => prev.map((u, idx) => idx === i ? { ...u, status: "uploading" } : u));
      try {
        await axios.post(`${API}/admin/photos/upload`, fd, {
          headers: { ...headers, "Content-Type": "multipart/form-data" },
          onUploadProgress: (e) => {
            const p = Math.round((e.loaded / (e.total || file.size)) * 100);
            setUploads((prev) => prev.map((u, idx) => idx === i ? { ...u, progress: p } : u));
          },
        });
        setUploads((prev) => prev.map((u, idx) => idx === i ? { ...u, status: "done", progress: 100 } : u));
      } catch (err) {
        setUploads((prev) => prev.map((u, idx) => idx === i ? { ...u, status: "error", error: err?.response?.data?.detail || "Upload failed" } : u));
      }
    }

    toast.success("Upload batch complete");
    fetchPhotos();
    setTimeout(() => setUploads([]), 3500);
  };

  const onDrop = (e) => {
    e.preventDefault();
    if (e.dataTransfer?.files?.length) handleFiles(e.dataTransfer.files);
  };

  const toggleFeatured = async (p) => {
    try {
      await axios.patch(`${API}/admin/photos/${p.id}`, { featured: !p.featured }, { headers });
      setPhotos((prev) => prev.map((x) => x.id === p.id ? { ...x, featured: !p.featured } : x));
    } catch { toast.error("Failed to update"); }
  };

  const updateField = async (p, field, value) => {
    try {
      await axios.patch(`${API}/admin/photos/${p.id}`, { [field]: value }, { headers });
      setPhotos((prev) => prev.map((x) => x.id === p.id ? { ...x, [field]: value } : x));
    } catch { toast.error("Failed to save"); }
  };

  const deletePhoto = async (p) => {
    if (!confirm(`Delete "${p.title || p.id}"? This can't be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/photos/${p.id}`, { headers });
      setPhotos((prev) => prev.filter((x) => x.id !== p.id));
      toast.success("Photo deleted");
    } catch { toast.error("Delete failed"); }
  };

  const fileUrl = (p, size = "medium") => `${API}/photos/file/${p.sizes?.[size] || p.sizes?.large}`;

  return (
    <div data-testid="photos-manager">
      {/* Upload zone */}
      <div
        onDrop={onDrop}
        onDragOver={(e) => e.preventDefault()}
        className="border-2 border-dashed border-white/15 rounded-3xl p-8 bg-white/5 hover:border-lime/60 transition cursor-pointer"
        onClick={() => fileInput.current?.click()}
        data-testid="photo-dropzone"
      >
        <input
          ref={fileInput}
          type="file"
          multiple
          accept="image/*"
          className="hidden"
          data-testid="photo-file-input"
          onChange={(e) => handleFiles(e.target.files)}
        />
        <div className="flex flex-col items-center text-center gap-2">
          <div className="w-12 h-12 rounded-2xl bg-lime text-dark grid place-items-center"><Upload size={20}/></div>
          <div className="font-display text-xl">Drop photos here or click to browse</div>
          <div className="text-sm text-white/60">JPG, PNG, HEIC — auto-converted to WebP, 3 sizes generated, max 25MB each</div>
          {(filterService || filterLocation) && (
            <div className="text-xs text-lime mt-1">New uploads will be tagged: {filterService || "any service"} · {filterLocation || "any location"}</div>
          )}
        </div>
      </div>

      {/* Upload progress */}
      {uploads.length > 0 && (
        <div className="mt-4 space-y-2" data-testid="upload-progress-list">
          {uploads.map((u) => (
            <div key={u.id} className="bg-white/5 border border-white/10 rounded-xl p-3 flex items-center gap-3">
              <div className="text-sm flex-1 truncate">{u.name}</div>
              <div className="text-xs text-white/60 w-20 text-right">
                {u.status === "done" ? <span className="text-lime flex items-center gap-1 justify-end"><Check size={12}/> Done</span>
                  : u.status === "error" ? <span className="text-red-400">Error</span>
                  : `${u.progress}%`}
              </div>
              <div className="w-32 h-1.5 bg-white/10 rounded-full overflow-hidden">
                <div className={`h-full ${u.status === "error" ? "bg-red-400" : "bg-lime"} transition-all`} style={{ width: `${u.progress}%` }} />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Filters */}
      <div className="mt-8 flex flex-wrap gap-3 items-center">
        <select
          value={filterService}
          onChange={(e) => setFilterService(e.target.value)}
          data-testid="filter-service"
          className="px-4 py-2 rounded-xl bg-white/10 border border-white/15 text-sm focus:outline-none focus:border-lime"
        >
          <option value="">All services</option>
          {SERVICES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select
          value={filterLocation}
          onChange={(e) => setFilterLocation(e.target.value)}
          data-testid="filter-location"
          className="px-4 py-2 rounded-xl bg-white/10 border border-white/15 text-sm focus:outline-none focus:border-lime"
        >
          <option value="">All locations</option>
          {LOCATIONS.map((l) => <option key={l} value={l}>{l}</option>)}
        </select>
        <div className="text-sm text-white/60 ml-auto" data-testid="photo-count">
          {loading ? "Loading…" : `${total} photo${total === 1 ? "" : "s"}`}
        </div>
      </div>

      {/* Gallery */}
      <div className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {photos.length === 0 && !loading && (
          <div className="col-span-full text-center py-16 text-white/50 flex flex-col items-center gap-3" data-testid="empty-state">
            <ImageIcon size={32} />
            <div>No photos yet. Drop some files above to start.</div>
          </div>
        )}
        {photos.map((p) => (
          <div key={p.id} className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden group" data-testid={`photo-card-${p.id}`}>
            <div className="relative aspect-[4/3] bg-black/40">
              <img src={fileUrl(p, "medium")} alt={p.alt} className="w-full h-full object-cover" loading="lazy" />
              <button
                onClick={() => toggleFeatured(p)}
                data-testid={`toggle-featured-${p.id}`}
                className={`absolute top-2 left-2 w-8 h-8 rounded-full grid place-items-center backdrop-blur ${p.featured ? "bg-lime text-dark" : "bg-black/50 text-white/80 hover:bg-black/70"}`}
                title={p.featured ? "Featured" : "Mark featured"}
              >
                <Star size={14} className={p.featured ? "fill-current" : ""} />
              </button>
              <button
                onClick={() => deletePhoto(p)}
                data-testid={`delete-${p.id}`}
                className="absolute top-2 right-2 w-8 h-8 rounded-full bg-black/50 hover:bg-red-500 grid place-items-center backdrop-blur"
                title="Delete"
              >
                <Trash2 size={14} />
              </button>
            </div>
            <div className="p-4 space-y-2.5">
              <input
                defaultValue={p.title}
                placeholder="Title"
                onBlur={(e) => e.target.value !== p.title && updateField(p, "title", e.target.value)}
                data-testid={`title-${p.id}`}
                className="w-full bg-transparent border-b border-white/10 pb-1 text-sm font-semibold focus:outline-none focus:border-lime"
              />
              <input
                defaultValue={p.alt}
                placeholder="Alt text (SEO)"
                onBlur={(e) => e.target.value !== p.alt && updateField(p, "alt", e.target.value)}
                data-testid={`alt-${p.id}`}
                className="w-full bg-transparent border-b border-white/10 pb-1 text-xs text-white/70 focus:outline-none focus:border-lime"
              />
              <div className="flex gap-2">
                <select
                  defaultValue={p.service}
                  onChange={(e) => updateField(p, "service", e.target.value)}
                  data-testid={`service-${p.id}`}
                  className="flex-1 bg-white/10 border border-white/10 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:border-lime"
                >
                  <option value="">No service</option>
                  {SERVICES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
                <select
                  defaultValue={p.location}
                  onChange={(e) => updateField(p, "location", e.target.value)}
                  data-testid={`location-${p.id}`}
                  className="flex-1 bg-white/10 border border-white/10 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:border-lime"
                >
                  <option value="">No location</option>
                  {LOCATIONS.map((l) => <option key={l} value={l}>{l}</option>)}
                </select>
              </div>
              <div className="text-[10px] text-white/40 truncate">ID: {p.id} · {p.width}×{p.height}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
